import { createDeserializer, handleJsonApiError } from "@jsonapi-serde/client";
import { queryOptions } from "@tanstack/react-query";
import { parsePhoneNumberWithError } from "libphonenumber-js";
import { z } from "zod/mini";
import { zj } from "zod-joda/mini";
import { apiUrl } from "@/utils/api.ts";
import { localZoneId } from "@/utils/datetime.ts";

const bidderSchema = z.object({
    firstName: z.string(),
    lastName: z.string(),
    emailAddress: z.string(),
    phoneNumber: z.pipe(
        z.string(),
        z.transform((value) => parsePhoneNumberWithError(value, "US")),
    ),
    streetAddress: z.string(),
    city: z.string(),
    state: z.string(),
    zipCode: z.string(),
    attendance: z.nullable(z.enum(["in_person", "zoom"])),
});

const optionalContactSchema = z.object({
    firstName: z.string(),
    lastName: z.string(),
    emailAddress: z.nullable(z.string()),
    phoneNumber: z.nullable(
        z.pipe(
            z.string(),
            z.transform((value) => parsePhoneNumberWithError(value, "US")),
        ),
    ),
    streetAddress: z.string(),
    city: z.string(),
    state: z.nullable(z.string()),
    zipCode: z.string(),
});

const openingBidSchema = z.object({
    target: z.enum(["parcel", "parcel_combination", "global"]),
    index: z.number(),
    amount: z.number(),
});

export const registrationAttributesSchema = z.object({
    registrationNumber: z.number(),
    createdAt: z.pipe(
        zj.zonedDateTime(),
        z.transform((value) => value.withZoneSameInstant(localZoneId)),
    ),
    bidder: bidderSchema,
    attorney: z.extend(optionalContactSchema, {
        lawFirm: z.string().check(z.trim()),
    }),
    lender: z.extend(optionalContactSchema, {
        financialInstitution: z.string().check(z.trim()),
    }),
    openingBids: z.array(openingBidSchema),
});

const deserializeRegistration = createDeserializer({
    type: "registration",
    cardinality: "one",
    attributesSchema: registrationAttributesSchema,
});

const deserializeRegistrations = createDeserializer({
    type: "registration",
    cardinality: "many",
    attributesSchema: z.extend(
        z.pick(registrationAttributesSchema, {
            registrationNumber: true,
            createdAt: true,
            bidder: true,
        }),
        {
            openingBids: z.optional(z.array(openingBidSchema)),
        },
    ),
});

export type Registration = ReturnType<typeof deserializeRegistration>["data"];
export type ListRegistration = ReturnType<typeof deserializeRegistrations>["data"][number];

type ListOptions = {
    includeOpeningBids: boolean;
};

export const createRegistrationQueryOptionsFactory = (authFetch: typeof fetch) => ({
    list: (auctionId: string, options?: ListOptions) =>
        queryOptions({
            queryKey: ["auction", auctionId, "registrations", options],
            queryFn: async ({ signal }) => {
                const fields = ["registrationNumber", "createdAt", "bidder"];

                if (options?.includeOpeningBids) {
                    fields.push("openingBids");
                }

                const url = apiUrl(`/auctions/${auctionId}/registrations`);
                url.searchParams.set("fields[registration]", fields.join(","));

                const response = await authFetch(url, { signal });
                await handleJsonApiError(response);
                return deserializeRegistrations(await response.json()).data;
            },
        }),
    get: (auctionId: string, registrationId: string) =>
        queryOptions({
            queryKey: ["auction", auctionId, "registration", registrationId],
            queryFn: async ({ signal }) => {
                const response = await authFetch(
                    apiUrl(`/auctions/${auctionId}/registrations/${registrationId}`),
                    { signal },
                );
                await handleJsonApiError(response);
                return deserializeRegistration(await response.json()).data;
            },
        }),
});
